<template>
    <modal-wrapper
        class="w-3xl"
        data-testid="sms-template-modal"
        :header="t('smsCommunication.template.title')"
        @close="emit('close')"
    >
        <div class="flex flex-col gap-y-4">
            <div class="flex items-center gap-x-4">
                <div class="grow flex relative">
                    <span class="absolute inset-y-0 left-3 flex items-center text-gray-400">
                        <icon name="regular/search" />
                    </span>

                    <input
                        class="grow pl-10 pr-4 py-2 border rounded-md bg-gray-50"
                        data-testid="sms-template-search"
                        :placeholder="t('smsCommunication.template.searchPlaceholder')"
                        type="text"
                        v-model="searchMessage"
                    />
                </div>

                <div class="w-48">
                    <activix-select
                        data-testid="sms-template-category-select"
                        :nullable="true"
                        :options="categories"
                        :placeholder="t('smsCommunication.template.selectCategory')"
                        v-model="selectedCategory"
                    />
                </div>
            </div>

            <div
                class="overflow-auto max-h-96 border rounded [container-type:inline-size]"
                data-testid="sms-template-container"
                ref="templatesContainer"
            >
                <template v-if="isLoading">
                    <grid-skeleton :n-columns="1" :n-rows="1" grid-columns-width="1fr" />

                    <grid-skeleton
                        data-testid="sms-template-loading"
                        :n-columns="6"
                        :n-rows="4"
                        grid-columns-width="2fr 2fr 4fr 4fr 1fr 1fr"
                    />
                </template>

                <table
                    class="table-auto text-sm [&_th]:p-3 [&_td]:p-3"
                    data-testid="sms-template-table"
                    v-else
                >
                    <colgroup>
                        <col :class="noMatchingTemplates ? 'w-1/6' : 'w-[1%]'" />
                        <col :class="noMatchingTemplates ? 'w-1/12' : 'w-[1%]'" />
                        <col class="w-1/2" />
                        <col class="w-1/2" />
                        <col :class="noMatchingTemplates ? 'w-1/12' : 'w-[1%]'" />
                    </colgroup>

                    <thead class="bg-gray-100 text-left text-xs text-gray-600">
                        <tr>
                            <th>
                                <div class="w-max max-w-[15cqw] break-words">
                                    {{ t('smsCommunication.template.table.name') }}
                                </div>
                            </th>

                            <th class="break-words md:whitespace-nowrap">
                                {{ t('smsCommunication.template.table.category') }}
                            </th>

                            <th>
                                {{ t('smsCommunication.template.table.message_en') }}
                            </th>

                            <th>
                                {{ t('smsCommunication.template.table.message_fr') }}
                            </th>

                            <th />
                        </tr>
                    </thead>

                    <tbody>
                        <tr
                            class="border-t"
                            :data-testid="`sms-template-row-${tpl.id}`"
                            :key="tpl.id"
                            v-for="tpl in filteredTemplates"
                        >
                            <td>
                                <div class="inline-block w-max max-w-[15cqw] break-words">
                                    {{ tpl.title }}
                                </div>
                            </td>

                            <td
                                class="break-words md:whitespace-nowrap"
                                :data-testid="`sms-template-category-${tpl.id}`"
                            >
                                {{ getCategoryLabel(tpl.response_template_custom_category_id) }}
                            </td>

                            <td>
                                <activix-tooltip
                                    :content="shouldShowTooltip(tpl, 'en') ? tpl.body_en : ''"
                                >
                                    <div
                                        :id="`content-en-${tpl.id}`"
                                        :data-testid="`sms-template-body-en-${tpl.id}`"
                                        class="line-clamp-3 break-words"
                                        v-html="tpl.body_en"
                                    />
                                </activix-tooltip>
                            </td>

                            <td>
                                <activix-tooltip
                                    :content="shouldShowTooltip(tpl, 'fr') ? tpl.body_fr : ''"
                                >
                                    <div
                                        :id="`content-fr-${tpl.id}`"
                                        :data-testid="`sms-template-body-fr-${tpl.id}`"
                                        class="line-clamp-3 break-words"
                                        v-html="tpl.body_fr"
                                    />
                                </activix-tooltip>
                            </td>

                            <td>
                                <div class="flex flex-col gap-2 lg:flex-row">
                                    <button
                                        class="px-3 py-1 border rounded bg-white text-sm whitespace-nowrap"
                                        :data-testid="`sms-template-insert-${tpl.id}`"
                                        @click="insertPreferredTemplate(tpl)"
                                        v-if="hasPreferredTranslation(tpl)"
                                    >
                                        {{ t('smsCommunication.template.insert') }}
                                    </button>

                                    <template v-else>
                                        <button
                                            class="px-3 py-1 border rounded bg-white text-sm whitespace-nowrap"
                                            :data-testid="`sms-template-insert-en-${tpl.id}`"
                                            @click="insertTemplate(tpl, 'en')"
                                        >
                                            {{ t('smsCommunication.template.insertEn') }}
                                        </button>

                                        <button
                                            class="px-3 py-1 border rounded bg-white text-sm whitespace-nowrap"
                                            :data-testid="`sms-template-insert-fr-${tpl.id}`"
                                            @click="insertTemplate(tpl, 'fr')"
                                        >
                                            {{ t('smsCommunication.template.insertFr') }}
                                        </button>
                                    </template>
                                </div>
                            </td>
                        </tr>

                        <tr v-if="noMatchingTemplates">
                            <td
                                colspan="5"
                                class="p-4 text-center text-gray-500"
                                data-testid="sms-template-no-results"
                            >
                                {{ t('smsCommunication.template.noResults') }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </modal-wrapper>
</template>

<script setup lang="ts">
    import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
    import { useAccountStore } from '@/store/modules/account/store.js';
    import { camelCase } from 'lodash-es';
    import ActivixSelect from '@/components/activix-ui/ActivixSelect/ActivixSelect.vue';
    import { useI18n } from '@/hooks/useI18n.ts';
    import { waitForRepaint } from '@/hooks/usePageRepaint.ts';
    import templates from '@/api/templates.js';
    import TemplateType from '@/entities/TemplateType.js';
    import ModalWrapper from '@/components/DynamicModalContentWrapper.vue';
    import GridSkeleton from '@/components/GridSkeleton.vue';

    type TemplateItem = {
        id: number;
        title: string;
        body_en: string;
        body_fr: string;
        response_template_custom_category_id: number;
        translations: TemplateTranslation[];
    };

    type TemplateResponseItem = {
        id: number;
        title: string | null;
        response_template_custom_category_id: number;
        translations?: TemplateTranslation[];
    };

    type TemplateTranslation = {
        locale: string;
        title: string;
        body: string;
    };

    type CustomCategory = {
        id: number;
        name: string;
        account_id?: number | null;
    };

    type CategoryOption = {
        label: string;
        value: number;
    };

    type SmsTemplateLocale = 'en' | 'fr';

    type Props = {
        onInsert: (templateId: number, locale: SmsTemplateLocale) => void | Promise<void>;
        preferredLocale?: SmsTemplateLocale;
    };

    const props = defineProps<Props>();

    type Emits = {
        (event: 'close'): void;
    };

    const emit = defineEmits<Emits>();

    const { t } = useI18n();

    const searchMessage = ref('');
    const selectedCategory = ref<number | null>(null);
    const templatesContainer = ref<HTMLElement | null>(null);

    const contextAccount = computed(() => useAccountStore().contextAccount);

    const templatesList = ref<TemplateItem[]>([]);
    const isLoading = ref(false);
    const noMatchingTemplates = computed(() => filteredTemplates.value.length === 0);

    const loadTemplates = async () => {
        isLoading.value = true;

        try {
            const data = (await templates.index(
                contextAccount.value.id,
                TemplateType.SMS
            )) as unknown as TemplateResponseItem[];

            templatesList.value = formatTemplates(data);
        } catch {
            templatesList.value = [];
        } finally {
            isLoading.value = false;
        }
    };

    const formatTemplates = (templates: TemplateResponseItem[]): TemplateItem[] => {
        return templates.map((template) => {
            const translations = template.translations || [];
            const en = translations.find((translation) => translation.locale === 'en');
            const fr = translations.find((translation) => translation.locale === 'fr');

            return {
                ...template,
                translations,
                title: template.title ?? '',
                body_en: en?.body || '',
                body_fr: fr?.body || '',
            };
        });
    };

    const filteredTemplates = computed(() => {
        let filtered = templatesList.value;

        if (selectedCategory.value) {
            filtered = filtered.filter(
                (template) =>
                    template.response_template_custom_category_id === selectedCategory.value
            );
        }

        if (!searchMessage.value.trim()) {
            return filtered;
        }

        return filtered.filter((template) => {
            const query = searchMessage.value.trim().toLowerCase();

            return (
                template.title.toLowerCase().includes(query) ||
                template.body_en.toLowerCase().includes(query) ||
                template.body_fr.toLowerCase().includes(query)
            );
        });
    });

    const insertTemplate = (template: TemplateItem, locale: SmsTemplateLocale) => {
        props.onInsert(template.id, locale);
        emit('close');
    };

    const hasPreferredTranslation = (template: TemplateItem): boolean => {
        return Boolean(
            props.preferredLocale &&
                template.translations.some(
                    (translation) => translation.locale === props.preferredLocale
                )
        );
    };

    const insertPreferredTemplate = (template: TemplateItem) => {
        if (!props.preferredLocale || !hasPreferredTranslation(template)) {
            return;
        }

        insertTemplate(template, props.preferredLocale);
    };

    const getCategoryLocaleName = (category: CustomCategory): string => {
        if (!category.account_id) {
            return t(`divisions.${camelCase(category.name)}`);
        }

        if (contextAccount.value.id !== category.account_id) {
            return `[Parent] ${category.name}`;
        }

        return category.name;
    };

    const customCategories = computed<CategoryOption[]>(() => {
        const categories = useAccountStore().config.customCategories as CustomCategory[];

        return categories.map((category) => ({
            label: getCategoryLocaleName(category),
            value: category.id,
        }));
    });

    const categories = computed<CategoryOption[]>(() => {
        if (!templatesList.value.length) {
            return [];
        }

        return customCategories.value.filter((category) => {
            return templatesList.value.some((template) => {
                return template.response_template_custom_category_id === category.value;
            });
        });
    });

    const categoryLabels = computed(() => {
        const labels = new Map<number, string>();

        for (const option of customCategories.value) {
            labels.set(option.value, option.label);
        }

        return labels;
    });

    const getCategoryLabel = (value: number): string => {
        return categoryLabels.value.get(value) ?? '';
    };

    const tooltipVisibility = ref<Record<string, boolean>>({});

    let resizeObserver: ResizeObserver | null = null;
    let updateInFlight = false;
    let updateQueued = false;

    const getTooltipKey = (template: TemplateItem, locale: 'en' | 'fr'): string => {
        return `${locale}-${template.id ?? ''}`;
    };

    const shouldShowTooltip = (template: TemplateItem, locale: 'en' | 'fr'): boolean => {
        return Boolean(tooltipVisibility.value[getTooltipKey(template, locale)]);
    };

    const updateTooltipVisibility = async () => {
        await waitForRepaint();

        const updated: Record<string, boolean> = {};

        for (const template of filteredTemplates.value) {
            const id = template.id ?? '';

            const enElement = document.getElementById(`content-en-${id}`) as HTMLElement | null;

            if (enElement) {
                updated[`en-${id}`] = enElement.scrollHeight > enElement.clientHeight;
            }

            const frElement = document.getElementById(`content-fr-${id}`) as HTMLElement | null;

            if (frElement) {
                updated[`fr-${id}`] = frElement.scrollHeight > frElement.clientHeight;
            }
        }

        tooltipVisibility.value = updated;
    };

    const scheduleTooltipUpdate = () => {
        if (updateInFlight) {
            updateQueued = true;
            return;
        }

        updateInFlight = true;

        updateTooltipVisibility().finally(() => {
            updateInFlight = false;

            if (updateQueued) {
                updateQueued = false;
                scheduleTooltipUpdate();
            }
        });
    };

    const setupResizeObserver = () => {
        if (!templatesContainer.value || resizeObserver) {
            return;
        }

        resizeObserver = new ResizeObserver(() => {
            scheduleTooltipUpdate();
        });

        resizeObserver.observe(templatesContainer.value);
    };

    const teardownResizeObserver = () => {
        if (!resizeObserver) {
            return;
        }

        resizeObserver.disconnect();
        resizeObserver = null;
    };

    watch(filteredTemplates, () => {
        scheduleTooltipUpdate();
    });

    watch(contextAccount, () => {
        loadTemplates();
    });

    onMounted(async () => {
        loadTemplates();

        await nextTick();
        setupResizeObserver();
        scheduleTooltipUpdate();
    });

    onBeforeUnmount(() => {
        teardownResizeObserver();
    });
</script>
